import { h, Host } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { throttle } from "../../utils/throttle";
export class Table {
    constructor() {
        this.useFixedColumn = true;
        this.fixedColIndex = 0;
        this.mobileTitleIndex = 0;
        this.maxFixedColWidth = 250;
        this.fullWidth = false;
        this.sortable = false;
        this.mobileSortLabel = "";
        this.hasFixedLink = false;
        this.mobileAccordion = true;
        this.noClickSelector = "";
        this.equalColumnWidth = false;
        this.striped = false;
        this.isFixed = false;
        this.fixedColWidth = this.useFixedColumn ? 250 : 0;
        this.leftShadow = false;
        this.rightShadow = false;
        this.uniqueId = undefined;
        this.fixedLinkSpaceAdded = false;
        this.fixedColumnHasMinWidth = true;
    }
    /** checks for IE11 for IntersectionObserver support */
    supportsIntersectionObserver = "IntersectionObserver" in window;
    element;
    contentObserver = new MutationObserver(debounce(() => this.onContentChange(), 100));
    handleIntersection = (entries) => {
        entries.map((entry) => {
            if (entry.isIntersecting) {
                this.onContentChange();
            }
        });
    };
    intersectionObserver = this.supportsIntersectionObserver ? new IntersectionObserver(this.handleIntersection) : null;
    table;
    innerDiv;
    mobileTable;
    connectedCallback() {
        this.uniqueId = `ux-table-id-${idCounter++}`;
        this.table = this.element.querySelector("table");
        window.addEventListener("resize", this.onResize);
    }
    componentDidLoad() {
        if (this.useFixedColumn) {
            this.addFixedLink();
            this.calculateFixedColWidth();
            this.calculateTableState();
            this.syncRowHeight();
        }
        this.calculateShadows();
        this.contentObserver.observe(this.innerDiv, {
            childList: true,
            subtree: true
        });
        if (!this.supportsIntersectionObserver) {
            return;
        }
        // @ts-expect-error Check for typing
        this.intersectionObserver.observe(this.innerDiv);
    }
    disconnectedCallback() {
        window.removeEventListener("resize", this.onResize);
        this.contentObserver.disconnect();
        if (!this.supportsIntersectionObserver) {
            return;
        }
        // @ts-expect-error Check for typing
        this.intersectionObserver.disconnect();
    }
    onContentChange() {
        if (this.useFixedColumn) {
            this.addFixedLink();
            this.syncRowHeight();
            this.calculateFixedColWidth();
            this.calculateTableState();
        }
        if (this.mobileAccordion) {
            this.mobileTable.update();
        }
        this.calculateShadows();
    }
    onResize = throttle(() => {
        this.calculateFixedColWidth();
        this.calculateTableState();
    }, 200);
    calculateTableState() {
        if (this.useFixedColumn) {
            const containerWidth = this.element.getBoundingClientRect().width;
            const tableRect = this.table.getBoundingClientRect();
            const tableWidth = tableRect.width + (this.isFixed ? this.fixedColWidth : 0);
            this.isFixed = containerWidth < tableWidth;
        }
    }
    calculateFixedColWidth() {
        const { table, fixedColIndex, maxFixedColWidth } = this;
        if (table.clientHeight === 0) {
            return;
        }
        const fixedCells = table.querySelectorAll(`td:nth-child(${fixedColIndex + 1})
            ,th:nth-child(${fixedColIndex + 1})`);
        const fixedCellsArray = [].slice.call(fixedCells);
        let width = 0;
        // we only calculate the width for the first 50 items to prevent that bigger tables get slow.
        fixedCellsArray.slice(0, 50).forEach((cell) => {
            const cellWidth = cell.getBoundingClientRect().width;
            width = cellWidth > width ? cellWidth : width;
        });
        if (!this.fixedLinkSpaceAdded && width !== 0 && this.hasFixedLink) {
            width = width + 15;
            this.fixedLinkSpaceAdded = true;
        }
        this.fixedColWidth = width > maxFixedColWidth || width === 0 ? maxFixedColWidth : width;
        this.fixedColumnHasMinWidth = this.fixedColWidth !== maxFixedColWidth;
    }
    onScroll = throttle(() => this.calculateShadows(), 10);
    calculateShadows() {
        const scrollLeft = this.innerDiv.scrollLeft;
        const containerWidth = this.innerDiv.getBoundingClientRect().width;
        const tableWidth = this.table.getBoundingClientRect().width + (this.isFixed ? this.fixedColWidth : 0);
        /** small puffer for the calculation of the right shadow, fixes the
         * bug that the shadow was not removed for 0.1px of discrepancy
         */
        const rightShadowPuffer = 2;
        this.leftShadow = (this.isFixed || !this.useFixedColumn) && scrollLeft > 0;
        this.rightShadow = scrollLeft + containerWidth + rightShadowPuffer < tableWidth;
    }
    /** the height of multi-line cells might differ from the fixed cell, so we need to sync it to prevent gaps */
    syncRowHeight() {
        const rows = this.table.querySelectorAll("tr");
        [].forEach.call(rows, (row) => {
            const rowHeight = row.getBoundingClientRect().height;
            // @ts-expect-error Check for typing
            const fixedCell = row.querySelector(`th:nth-child(${this.fixedColIndex + 1}),td:nth-child(${this.fixedColIndex + 1})`);
            const fixedCellHeight = fixedCell.getBoundingClientRect().height;
            const height = Math.max(rowHeight, fixedCellHeight);
            row.style.height = `${height}px`;
            fixedCell.style.height = `${height}px`;
        });
    }
    sortRows(e) {
        if (!this.sortable) {
            return;
        }
        const numerical = e.detail.numerical;
        const sorter = e.target;
        const th = sorter.closest("th");
        // @ts-expect-error Check for typing
        const titleRow = th.parentElement;
        // @ts-expect-error Check for typing
        const colIndex = [].indexOf.call(titleRow.children, th);
        // @ts-expect-error Check for typing
        const table = th.closest("table");
        const sortAttr = "data-sort";
        const getCellValue = (tr, idx) => tr.children[idx].hasAttribute(sortAttr) ? tr.children[idx].getAttribute(sortAttr) : tr.children[idx].textContent;
        const comparer = (idx, asc) => (a, b) => {
            return numerical
                ? // @ts-expect-error Check for typing
                    getCellValue(asc ? a : b, idx).localeCompare(getCellValue(asc ? b : a, idx), undefined, { numeric: true })
                : // @ts-expect-error Check for typing
                    getCellValue(asc ? a : b, idx).localeCompare(getCellValue(asc ? b : a, idx));
        };
        [].slice
            // @ts-expect-error Check for typing
            .call(table.querySelectorAll(`tr`))
            .filter((tr) => !tr.isSameNode(titleRow))
            .sort(comparer(colIndex, sorter.sortedDirection === "asc"))
            // @ts-expect-error Check for typing
            .forEach((tr) => table.appendChild(tr));
    }
    addFixedLink() {
        if (!this.hasFixedLink) {
            return;
        }
        const { table } = this;
        const tableRows = table.querySelectorAll(`tr`);
        const tableRowsArray = [].slice.call(tableRows);
        const fixedLinkClass = "ux-table__fixed-link";
        tableRowsArray.forEach((tableRow) => {
            const lastCellIndex = tableRow.children.length - 1;
            const lastRowCell = tableRow.children[lastCellIndex];
            const link = lastRowCell.querySelector("a");
            if (!link) {
                return;
            }
            const firstRowCell = tableRow.children[this.fixedColIndex];
            const hasFixedLink = firstRowCell.querySelector(`.${fixedLinkClass}`);
            if (!hasFixedLink) {
                const clonedLink = link.cloneNode(true);
                clonedLink.className = fixedLinkClass;
                clonedLink.innerHTML = '<ux-icon name="chevron-right" weight="normal"></ux-icon>';
                firstRowCell.insertAdjacentElement("beforeend", clonedLink);
            }
        });
    }
    render() {
        const baseClass = "ux-table";
        const fixedClass = `${baseClass}--is-fixed`;
        const { uniqueId, isFixed, fixedColIndex, fixedColWidth, maxFixedColWidth, leftShadow, rightShadow, fullWidth, fixedColumnHasMinWidth, useFixedColumn, mobileAccordion, equalColumnWidth } = this;
        const fixedCol = fixedColIndex + 1; // CSS selectors work with an 1-based index
        const { table } = this;
        const lastRow = table.rows[table.rows.length - 1];
        const lastCellIndex = lastRow.cells[lastRow.cells.length - 1].cellIndex;
        const columnCount = table.rows[0].cells.length;
        return (h(Host, { key: '7e903ead083b24da9ec51bd515293c42c3681334', class: {
                [baseClass]: true
            } }, h("style", { key: '1ae1eca9a2156611add53c28962ad60756408f7b' }, `
                    #${uniqueId} th:nth-child(${fixedCol}),
                    #${uniqueId} td:nth-child(${fixedCol}) {
                        max-width: ${maxFixedColWidth}px;
                    }

                    .${fixedClass} #${uniqueId} th:nth-child(${fixedCol}),
                    .${fixedClass} #${uniqueId} td:nth-child(${fixedCol}) {
                        position: absolute;
                        left: 0;
                        width: ${fixedColWidth}px;
                        min-width: ${fixedColumnHasMinWidth ? `fit-content` : `auto`};
                        display:flex;
                        align-items: center;
                        justify-content: flex-start;
                        border-left: 1px solid transparent;
                    }

                    .${fixedClass} #${uniqueId} th:nth-child(${fixedCol}) {
                        border-right: 1px solid #9ea9b0;
                    }

                    .${fixedClass} #${uniqueId} td:nth-child(${fixedCol}) {
                        border-top: 1px solid transparent;
                        border-right: 1px solid #cbcbcb;
                    }

                    .${fixedClass} #${uniqueId} th:nth-child(${lastCellIndex === fixedColIndex ? lastCellIndex : 0}),
                    .${fixedClass} #${uniqueId} td:nth-child(${lastCellIndex === fixedColIndex ? lastCellIndex : 0}) {
                        border-right: 1px solid transparent !important;
                    }

                    .${fixedClass} #${uniqueId} table {
                        margin-left: ${fixedColWidth}px;
                    }

                    #${uniqueId}:before {
                        left: ${fixedColWidth}px;
                    }

                    ${equalColumnWidth
            ? `td {
                            width: ${100 / columnCount}%;
                        }`
            : ""}

                `), h("div", { key: '0fa212b11cfa35431768324129fab6c29f4284fc', class: {
                [`${baseClass}--hide-on-mobile`]: mobileAccordion,
                [fixedClass]: isFixed,
                [`${baseClass}--has-left-shadow`]: leftShadow,
                [`${baseClass}--non-fixed-column`]: !useFixedColumn,
                [`${baseClass}--has-right-shadow`]: rightShadow,
                [`${baseClass}--full-width`]: fullWidth,
                [`${baseClass}--same-column-width`]: equalColumnWidth
            } }, h("div", { key: '3e1c1f2afd83cb22d1b25709d8af29fbfec4c71d', class: `${baseClass}__inner`,
            // @ts-expect-error Check for typing
            ref: (el) => (this.innerDiv = el), onScroll: () => this.onScroll(), id: uniqueId, "data-scroll-target": true }, h("slot", { key: 'b1c67fd9a902585f49433523b1e21390bc4e0fd7' }))), mobileAccordion && (h("ux-mobile-table", { key: '4f39d435492f6733bc8656d807893dbc03de6e6f', sortable: this.sortable,
            // @ts-expect-error Check for typing
            ref: (el) => (this.mobileTable = el), uxTable: this.element, selectLabel: this.mobileSortLabel, noClickSelector: this.noClickSelector }))));
    }
    static get is() { return "ux-table"; }
    static get originalStyleUrls() {
        return {
            "$": ["table.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["table.css"]
        };
    }
    static get properties() {
        return {
            "useFixedColumn": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "flag which determines if fixed column mechanism should be used"
                },
                "attribute": "use-fixed-column",
                "reflect": false,
                "defaultValue": "true"
            },
            "fixedColIndex": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "zero-based index of the column that should be fixed on smaller viewports - defaults to the first col"
                },
                "attribute": "fixed-col-index",
                "reflect": false,
                "defaultValue": "0"
            },
            "mobileTitleIndex": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "zero-based index of the column that should be used for the title column for the mobile version\n- defaults to the first col"
                },
                "attribute": "mobile-title-index",
                "reflect": false,
                "defaultValue": "0"
            },
            "maxFixedColWidth": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "maximum number of pixels that the fixed column is allowed to be wide"
                },
                "attribute": "max-fixed-col-width",
                "reflect": false,
                "defaultValue": "250"
            },
            "fullWidth": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the table should always be at least 100% wide"
                },
                "attribute": "full-width",
                "reflect": false,
                "defaultValue": "false"
            },
            "sortable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the table should sort its static data by itself. Do not use this if the data needs to be sorted by some\nlogic (i.e. via an API)"
                },
                "attribute": "sortable",
                "reflect": false,
                "defaultValue": "false"
            },
            "mobileSortLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Label for the ux-select that is shown for the sorting of mobile tables"
                },
                "attribute": "mobile-sort-label",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "hasFixedLink": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The link in the last column will be added into the fixed column on smaller viewports"
                },
                "attribute": "has-fixed-link",
                "reflect": false,
                "defaultValue": "false"
            },
            "mobileAccordion": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "setting this to false disables the mobile accordion - showing the scrollable table instead also on smaller screens"
                },
                "attribute": "mobile-accordion",
                "reflect": false,
                "defaultValue": "true"
            },
            "noClickSelector": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "no-click-selector for ux-accordion in mobile table \u2013 use if you use an icon in the mobile-fixed-col"
                },
                "attribute": "no-click-selector",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "equalColumnWidth": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "makes every column the same width, no matter the content"
                },
                "attribute": "equal-column-width",
                "reflect": false,
                "defaultValue": "false"
            },
            "striped": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "makes background colors of the table rows change"
                },
                "attribute": "striped",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "isFixed": {},
            "fixedColWidth": {},
            "leftShadow": {},
            "rightShadow": {},
            "uniqueId": {},
            "fixedLinkSpaceAdded": {},
            "fixedColumnHasMinWidth": {}
        };
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "isFixed",
                "methodName": "calculateShadows"
            }, {
                "propName": "isFixed",
                "methodName": "syncRowHeight"
            }];
    }
    static get listeners() {
        return [{
                "name": "uxTableSort",
                "method": "sortRows",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
/** this variable is incremented by each instance */
let idCounter = 0;
//# sourceMappingURL=table.js.map
